/**
 * Manages Swiper slideshow initialization and event handling.
 * Handles slide navigation, quiz logic, and exit gestures.
 */
import Swiper                   from 'swiper';
import { Mousewheel, EffectFade } from 'swiper/modules';

class SlidesManager {
    constructor({
        scrollSectionElement,
        navigationController
    }) {
        this.scrollSectionElement = scrollSectionElement;
        this.navigationController = navigationController;
        this.swiper               = null;
        this.answeredSections     = new Set();
        this.lastSlideChangeTime  = Date.now();
        this.exitWheelDelta       = 0;

        // Constants
        this.EXIT_WHEEL_THRESHOLD      = 120;
        this.EXIT_WHEEL_TIME_THRESHOLD = 400;
        this.EXIT_TOUCH_DISTANCE       = 40;

        // Bind methods
        this.handleOverlayShown        = this.handleOverlayShown.bind(this);
        this.handleContinue            = this.handleContinue.bind(this);
        this.handleSlideChange         = this.handleSlideChange.bind(this);
        this.handleWheelToExit         = this.handleWheelToExit.bind(this);
        this.handleTouchStart          = this.handleTouchStart.bind(this);
        this.handleTouchEnd            = this.handleTouchEnd.bind(this);
        this.handleManualContinueClick = this.handleManualContinueClick.bind(this);

        this.touchStartY = 0;
    }

    /**
     * Initialize Swiper and attach event listeners.
     */
    init() {
        if (!this.scrollSectionElement) {
            return null;
        }

        const slidesContainer = this.scrollSectionElement.querySelector('[data-experience-slides]');

        if (!slidesContainer) {
            return null;
        }

        const slidesInnerElement = this.scrollSectionElement.querySelector('.experience-scroll__inner');

        this.swiper = new Swiper(slidesContainer, {
            modules       : [Mousewheel, EffectFade],
            direction     : 'vertical',
            slidesPerView : 1,
            speed         : 900,
            effect        : 'fade',
            fadeEffect    : {
                crossFade : true
            },
            mousewheel : {
                enabled        : true,
                releaseOnEdges : true
            },
            allowSlideNext : false,
            allowSlidePrev : true
        });

        this.swiper.on('slideChange', this.handleSlideChange);

        if (slidesInnerElement) {
            slidesInnerElement.classList.add('experience-scroll__inner--ready');
        }

        window.addEventListener('experience-quiz:overlay-shown', this.handleOverlayShown);
        window.addEventListener('experience-quiz:continue', this.handleContinue);

        slidesContainer.addEventListener('wheel', this.handleWheelToExit, { passive : false });
        slidesContainer.addEventListener('touchstart', this.handleTouchStart, { passive : true });
        slidesContainer.addEventListener('touchend', this.handleTouchEnd, { passive : true });

        const manualContinueButtons = this.scrollSectionElement.querySelectorAll('[data-experience-slide-continue]');

        manualContinueButtons.forEach((button) => {
            button.addEventListener('click', this.handleManualContinueClick);
        });

        const overlayTextElements = this.scrollSectionElement.querySelectorAll('.experience-quiz-result__text, .experience-summary__inner, .experience-final__inner');

        const hasVerticalScroll = (element) => {
            if (!element) {
                return false;
            }

            return element.scrollHeight > element.clientHeight + 1;
        };

        overlayTextElements.forEach((element) => {
            const scrollElement = element.querySelector('.simplebar-content-wrapper') || element;

            const stopWheelIfScrollable = (event) => {
                if (!hasVerticalScroll(scrollElement)) {
                    return;
                }

                event.stopPropagation();
            };

            const stopTouchIfScrollable = (event) => {
                if (!hasVerticalScroll(scrollElement)) {
                    return;
                }

                event.stopPropagation();

                if (event.type !== 'touchstart' || !this.swiper) {
                    return;
                }

                this.swiper.allowTouchMove = false;

                const unlockTouchMove = () => {
                    if (!this.swiper) {
                        return;
                    }

                    this.swiper.allowTouchMove = true;
                };

                window.addEventListener('touchend', unlockTouchMove, { passive : true, once : true });
                window.addEventListener('touchcancel', unlockTouchMove, { passive : true, once : true });
            };

            // Store handler references in a Map for cleanup
            if (!this._overlayHandlers) {
                this._overlayHandlers = new Map();
            }
            this._overlayHandlers.set(element, {
                wheel : stopWheelIfScrollable,
                touchStart : stopTouchIfScrollable,
                touchMove : stopTouchIfScrollable
            });

            scrollElement.addEventListener('wheel', stopWheelIfScrollable, { passive : true });
            scrollElement.addEventListener('touchstart', stopTouchIfScrollable, { passive : true, capture : true });
            scrollElement.addEventListener('touchmove', stopTouchIfScrollable, { passive : true, capture : true });
        });

        this.updateSlideLocks();

        return this.swiper;
    }

    /**
     * Get active section ID from current slide.
     */
    getActiveSectionId() {
        if (!this.swiper) {
            return null;
        }

        const hasSlides   = Array.isArray(this.swiper.slides) && this.swiper.slides.length > 0;
        const activeSlide = hasSlides ? this.swiper.slides[this.swiper.activeIndex] : null;

        return activeSlide ? activeSlide.getAttribute('data-experience-section-id') : null;
    }

    /**
     * Update slide navigation locks based on answered sections.
     */
    updateSlideLocks() {
        if (!this.swiper) {
            return;
        }

        const sectionId = this.getActiveSectionId();

        if (!sectionId) {
            this.swiper.allowSlideNext = true;
            return;
        }

        this.swiper.allowSlideNext = this.answeredSections.has(sectionId);
    }

    /**
     * Handle quiz overlay shown event.
     */
    handleOverlayShown(event) {
        const detail      = event.detail || {};
        const { sectionId } = detail;

        if (!sectionId) {
            return;
        }

        this.answeredSections.add(sectionId);

        const activeSectionId = this.getActiveSectionId();

        if (activeSectionId === sectionId && this.swiper) {
            const UNLOCK_DELAY_MS = 900;

            window.setTimeout(() => {
                if (!this.swiper) {
                    return;
                }

                const currentSectionId = this.getActiveSectionId();

                if (currentSectionId !== sectionId) {
                    return;
                }

                this.swiper.allowSlideNext = true;
            }, UNLOCK_DELAY_MS);
        }
    }

    /**
     * Handle quiz continue event.
     */
    handleContinue(event) {
        const detail      = event.detail || {};
        const { sectionId } = detail;

        const activeSectionId = this.getActiveSectionId();

        if (!sectionId || activeSectionId !== sectionId) {
            return;
        }

        if (this.swiper) {
            this.swiper.slideNext();
        }
    }

    /**
     * Handle slide change event.
     */
    handleSlideChange() {
        this.lastSlideChangeTime = Date.now();
        this.updateSlideLocks();
    }

    /**
     * Handle wheel event to exit from first slide.
     */
    handleWheelToExit(event) {
        if (!this.navigationController || !this.swiper) {
            return;
        }

        if (this.swiper.activeIndex !== 0) {
            this.exitWheelDelta = 0;
            return;
        }

        const now = Date.now();

        if (now - this.lastSlideChangeTime < this.EXIT_WHEEL_TIME_THRESHOLD) {
            return;
        }

        if (event.deltaY >= 0) {
            this.exitWheelDelta = 0;
            return;
        }

        event.preventDefault();

        this.exitWheelDelta += event.deltaY;

        if (this.exitWheelDelta <= -this.EXIT_WHEEL_THRESHOLD) {
            this.exitWheelDelta = 0;
            this.navigationController.navigate(-1);
        }
    }

    /**
     * Handle touch start event.
     */
    handleTouchStart(event) {
        if (!event.changedTouches || event.changedTouches.length === 0) {
            return;
        }

        const touch = event.changedTouches[0];

        this.touchStartY = touch.clientY;
    }

    /**
     * Handle touch end event to exit from first slide.
     */
    handleTouchEnd(event) {
        if (!this.navigationController || !this.swiper) {
            return;
        }

        if (this.swiper.activeIndex !== 0) {
            return;
        }

        if (!event.changedTouches || event.changedTouches.length === 0) {
            return;
        }

        const touch  = event.changedTouches[0];
        const deltaY = touch.clientY - this.touchStartY;

        if (deltaY <= this.EXIT_TOUCH_DISTANCE) {
            return;
        }

        this.navigationController.navigate(-1);
    }

    handleManualContinueClick(event) {
        if (!this.swiper) {
            return;
        }

        const targetButton = event.currentTarget;

        if (!targetButton) {
            return;
        }

        const slideElement = targetButton.closest('.experience-scroll__slide');

        if (!slideElement) {
            return;
        }

        const sectionId = slideElement.getAttribute('data-experience-section-id');

        if (sectionId) {
            this.answeredSections.add(sectionId);
        }

        const activeSectionId = this.getActiveSectionId();

        if (sectionId && activeSectionId === sectionId) {
            this.swiper.allowSlideNext = true;
        }

        this.swiper.slideNext();
    }

    destroy() {
        // Clean up Swiper instance
        if (this.swiper) {
            this.swiper.destroy();
            this.swiper = null;
        }

        // Remove window event listeners
        window.removeEventListener('experience-quiz:overlay-shown', this.handleOverlayShown);
        window.removeEventListener('experience-quiz:continue', this.handleContinue);

        // Remove container event listeners
        if (this.scrollSectionElement) {
            const slidesContainer = this.scrollSectionElement.querySelector('[data-experience-slides]');

            if (slidesContainer) {
                slidesContainer.removeEventListener('wheel', this.handleWheelToExit);
                slidesContainer.removeEventListener('touchstart', this.handleTouchStart);
                slidesContainer.removeEventListener('touchend', this.handleTouchEnd);
            }

            // Remove button listeners
            const manualContinueButtons = this.scrollSectionElement.querySelectorAll('[data-experience-slide-continue]');
            manualContinueButtons.forEach((button) => {
                button.removeEventListener('click', this.handleManualContinueClick);
            });

            // Remove overlay text element listeners
            const overlayTextElements = this.scrollSectionElement.querySelectorAll('.experience-quiz-result__text, .experience-summary__inner, .experience-final__inner');
            overlayTextElements.forEach((element) => {
                const handlers = this._overlayHandlers?.get(element);
                if (handlers) {
                    const scrollElement = element.querySelector('.simplebar-content-wrapper') || element;
                    scrollElement.removeEventListener('wheel', handlers.wheel);
                    scrollElement.removeEventListener('touchstart', handlers.touchStart);
                    scrollElement.removeEventListener('touchmove', handlers.touchMove);
                }
            });

            // Clear overlay handlers map
            if (this._overlayHandlers) {
                this._overlayHandlers.clear();
                this._overlayHandlers = null;
            }
        }

        // Reset state
        this.answeredSections.clear();
        this.scrollSectionElement = null;
        this.navigationController = null;
    }
}

export default SlidesManager;
